import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Signup.css';

const Signup = ({ onLogin }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    phone: '',
    department: '',
    password: '',
    confirm_password: '',
    
    // Company info (required for company registration)
    company: {
      company_name: '',
      company_email: '',
      company_phone: '',
      company_address: '',
      industry: '',
      company_size: '',
      contact_person: '',
      contact_person_phone: ''
    }
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('company.')) {
      const companyField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        company: {
          ...prev.company,
          [companyField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const validateForm = () => {
    const { full_name, username, email, password, confirm_password, company } = formData;
    
    // Validate personal info
    if (!full_name.trim()) {
      setError('Full name is required');
      return false;
    }
    
    if (!username.trim()) {
      setError('Username is required');
      return false;
    }
    
    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }
    
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (!password) {
      setError('Password is required');
      return false;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    
    if (!/(?=.*\d)/.test(password)) {
      setError('Password must contain at least one number');
      return false;
    }
    
    if (password !== confirm_password) {
      setError('Passwords do not match');
      return false;
    }
    
    // Validate company info (required for all registrations)
    if (!company.company_name.trim()) {
      setError('Company name is required');
      return false;
    }
    
    if (!company.company_email.trim()) {
      setError('Company email is required');
      return false;
    }
    
    if (!emailRegex.test(company.company_email)) {
      setError('Please enter a valid company email address');
      return false;
    }
    
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      // Prepare registration data matching your backend structure
      const registrationData = {
        action: 'register',
        full_name: formData.full_name,
        username: formData.username,
        email: formData.email,
        phone: formData.phone || null,
        department: formData.department || null,
        password: formData.password,
        confirm_password: formData.confirm_password,
        company: {
          company_name: formData.company.company_name,
          company_email: formData.company.company_email,
          company_phone: formData.company.company_phone || null,
          company_address: formData.company.company_address || null,
          industry: formData.company.industry || null,
          company_size: formData.company.company_size || null,
          contact_person: formData.company.contact_person || formData.full_name,
          contact_person_phone: formData.company.contact_person_phone || formData.phone || null
        }
      };

      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      if (data.success) {
        setSuccess(data.message || 'Company registration submitted! Please wait for admin approval.');
        
        // If token is provided, login immediately
        if (data.token) {
          localStorage.setItem('token', data.token);
          onLogin(data.user, data.token);
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          // If approval is required, redirect to login
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (error) {
      setError(error.message || 'Connection error. Please try again.');
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <div className="logo-container">
            <div className="logo-icon">🚛</div>
            <h1>Vehicle Tracking System</h1>
          </div>
          <p className="signup-subtitle">Company Registration</p>
          <p className="signup-note">Register your company and create admin account</p>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          {error && (
            <div className="alert alert-danger">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}
          
          {success && (
            <div className="alert alert-success">
              <span>✅</span>
              <span>{success}</span>
            </div>
          )}

          <div className="form-section">
            <h3>Company Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company.company_name">Company Name *</label>
                <input
                  type="text"
                  id="company.company_name"
                  name="company.company_name"
                  value={formData.company.company_name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="company.company_email">Company Email *</label>
                <input
                  type="email"
                  id="company.company_email"
                  name="company.company_email"
                  value={formData.company.company_email}
                  onChange={handleInputChange}
                  placeholder="company@example.com"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company.company_phone">Company Phone</label>
                <input
                  type="tel"
                  id="company.company_phone"
                  name="company.company_phone"
                  value={formData.company.company_phone}
                  onChange={handleInputChange}
                  placeholder="Company phone number"
                />
              </div>
              {/* 
              <div className="form-group">
                <label htmlFor="company.industry">Industry</label>
                <select
                  id="company.industry"
                  name="company.industry"
                  value={formData.company.industry}
                  onChange={handleInputChange}
                >
                  <option value="">Select Industry</option>
                  <option value="logistics">Logistics & Transportation</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="retail">Retail</option>
                  <option value="construction">Construction</option>
                  <option value="agriculture">Agriculture</option>
                  <option value="other">Other</option>
                </select>
              </div> */}
              <div className="form-group">
              <label htmlFor="company.company_address">Company Address</label>
              <textarea
                id="company.company_address"
                name="company.company_address"
                value={formData.company.company_address}
                onChange={handleInputChange}
                placeholder="Enter company address"
                rows="2"
              />
            </div>
            </div>

           {/*  <div className="form-group">
              <label htmlFor="company.company_size">Company Size</label>
              <select
                id="company.company_size"
                name="company.company_size"
                value={formData.company.company_size}
                onChange={handleInputChange}
              >
                <option value="">Select Size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501+">501+ employees</option>
              </select>
            </div>
 */}
            {/* <div className="form-group">
              <label htmlFor="company.company_address">Company Address</label>
              <textarea
                id="company.company_address"
                name="company.company_address"
                value={formData.company.company_address}
                onChange={handleInputChange}
                placeholder="Enter company address"
                rows="2"
              />
            </div> */}
          </div>

          <div className="form-section">
            <h3>Admin Account (Primary Contact)</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="full_name">Full Name *</label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Choose a username"
                  required
                />
                <small>Must be at least 3 characters</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

           {/*  <div className="form-group">
              <label htmlFor="department">Department</label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="Enter your department"
              />
            </div> */}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a password"
                  required
                />
                <div className="password-requirements">
                  <p>Password must contain:</p>
                  <ul>
                    <li className={formData.password.length >= 8 ? 'valid' : ''}>
                      At least 8 characters
                    </li>
                    <li className={/(?=.*[a-z])/.test(formData.password) ? 'valid' : ''}>
                      One lowercase letter
                    </li>
                    <li className={/(?=.*[A-Z])/.test(formData.password) ? 'valid' : ''}>
                      One uppercase letter
                    </li>
                    <li className={/(?=.*\d)/.test(formData.password) ? 'valid' : ''}>
                      One number
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="confirm_password">Confirm Password *</label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  required
                />
                {formData.password && formData.confirm_password && (
                  <div className={`password-match ${formData.password === formData.confirm_password ? 'valid' : 'invalid'}`}>
                    {formData.password === formData.confirm_password ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </div>
                )}
              </div>
            </div>
          </div>

       {/*    <div className="terms-agreement">
            <label className="checkbox-label">
              <input type="checkbox" required />
              <span className="checkmark"></span>
              I confirm that I am an authorized representative of this company and agree to the <Link to="/terms">Terms of Service</Link>
            </label>
          </div> */}

          <button 
            type="submit" 
            className="btn-submit" 
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Registering Company...
              </>
            ) : 'Register Company & Create Admin Account'}
          </button>
        </form>
        <div className="signup-footer">
          <p>
            Already have an account? <Link to="/login" className="login-link">Login here</Link>
          </p>
          <p className="signup-help">
            Individual user accounts must be created by company administrators.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;